import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { handleAuthentication, showSnackBar } from "@containers/App/reducer"
import {
    parsePageInfoWidget,
    parseWidgetData,
    parseETASelectionWidgetData,
    parseSkuList,
    changeEtaSelectedItem,
    parseCarePlanDialogData,
} from "./parser"

const initialState = {
    isLoading: false,
    isFetched: false,
    error: null,
    widgets: {},
    pageInfoWidgets: {},
    selectedOptionId: null,
    etaSelectionData: {},
    listOfItems: {},
    analyticsData: {},
    carePlanDialogRawData: {},
}

export const shipmentPageSlice = createSlice({
    name: "shipmentPage",
    initialState: initialState,
    reducers: {
        reset: () => initialState,
        setEtaSelectionData: (state, action) => {
            state.etaSelectionData = parseETASelectionWidgetData(
                action.payload?.data,
                action.payload?.optionId,
                action.payload?.deliverySpeedId,
                action.payload?.shipmentId
            )
        },
        setEtaSelectionDataForNoShipmentOptions: (state, action) => {
            state.etaSelectionData = changeEtaSelectedItem(
                action.payload.data,
                action.payload?.deliverySpeedId,
                action.payload?.shipmentId
            )
        },
        setListOfItems: (state, action) => {
            state.listOfItems = parseSkuList(action.payload)
        },
    },
    extraReducers: (builder) => {
        //handle asynchronous actions
        builder.addCase(fetchShipmentData.fulfilled, (state, action) => {
            state.isLoading = false
            state.isFetched = true
            state.error = false
            let data = parseWidgetData(action.payload?.widgets)
            state.widgets = data.widgetData
            state.selectedOptionId = data.selectedOptionId
            state.etaSelectionData = data.etaSelectionData
            state.pageInfoWidgets = parsePageInfoWidget(action.payload)
            state.analyticsData = action.payload?.allocation_analytics_data || {}
            state.carePlanDialogRawData = action.payload.care_plan_upsell ?? {}
        }),
            builder.addCase(fetchShipmentData.pending, (state) => {
                state.isLoading = true
                state.isFetched = false
                state.error = false
            }),
            builder.addCase(fetchShipmentData.rejected, (state) => {
                state.isLoading = false
                state.error = true
                state.isFetched = false
            }),
            builder.addCase(fetchCarePlanDialogData.fulfilled, (state, action) => {
                state.pageInfoWidgets.carePlanDialogData = parseCarePlanDialogData(action.payload)
            })
    },
})

export const fetchShipmentData = createAsyncThunk(
    "shipmentPageSlice/fetchShipmentPageData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        try {
            const res = await api.get(`api/v4/carts/shipments?types=pharmacy`)
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
                if (err.error?.errors?.[0]?.target_url) {
                    document.location.replace(err.error.errors[0].target_url)
                } else document.location.replace("/")
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

export const selectShipment = createAsyncThunk(
    "shipmentPageSlice/selectShipmentData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { payload } = _arg
        const successCallback = _arg?.successCallback
        try {
            const res = await api.put(`api/v4/carts/shipments/update-preferences?types=pharmacy`, payload)
            await Promise.resolve()
            if (typeof successCallback === "function") successCallback()
            return Promise.resolve(res?.data)
        } catch (err) {
            const errorCallback = _arg?.errorCallback
            if (typeof errorCallback === "function") errorCallback()
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

export const fetchCarePlanDialogData = createAsyncThunk(
    "cartSummaryPage/fetchCarePlanDialogData",
    async (arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { carePlanDialogRawData, selectionData } = arg
        try {
            const cartType = "PHARMACY"
            const res = await api.post(`/api/v4/shipment_upsell`, {
                types: cartType,
                care_plan_upsell: carePlanDialogRawData,
                selected_shipments: selectionData,
            })
            return Promise.resolve(res?.data?.care_plan_upsell)
        } catch (err) {
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                if (err.error.message) {
                    showSnackBar(dispatch, err.error.message)
                }
            }
            const onServer = typeof window === "undefined"
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err.error)
        }
    }
)

export const shipmentPageReducer = shipmentPageSlice.reducer
export const { reset, setEtaSelectionData, setListOfItems, setEtaSelectionDataForNoShipmentOptions } =
    shipmentPageSlice.actions
